import {useTheme} from '@src/core/theme/theme';
import {ComponentProps} from 'react';
import styled, {css} from 'styled-components';
import Image from 'next/image';
import {LoadingDots} from '../ui/LoadingState';

type ButtonTypeT =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'grey'
  | 'with-icon'
  | 'border';

type ContainerProps = {
  children?: JSX.Element;
  buttonType?: ButtonTypeT;
};

const solidStyles = css`
  /* Full-bleed shorthand — avoid background-size: 150% (half-fill on some
     mobile WebKit devices). */
  background: ${(p) => p.theme.gradients.brand};
  background-repeat: no-repeat;
  background-size: 100% 100%;
  color: ${(p) => p.theme.colors.neutral100};
  box-shadow: ${(p) => p.theme.elevation.brand},
    ${(p) => p.theme.elevation.inset};

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.gradients.brandHover};
    background-repeat: no-repeat;
    background-size: 100% 100%;
    box-shadow: ${(p) => p.theme.elevation.brandHover},
      ${(p) => p.theme.elevation.inset};
  }
`;

const softStyles = css`
  background: ${(p) => p.theme.colors.neutral100};
  color: ${(p) => p.theme.colors.primary500};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: ${(p) => p.theme.elevation.sm};

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.colors.violet300};
    background: ${(p) => p.theme.colors.primary100};
    box-shadow: ${(p) => p.theme.elevation.md};
  }
`;

const greyStyles = css`
  background: ${(p) => p.theme.colors.surfaceSunken};
  color: ${(p) => p.theme.colors.primary500};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: ${(p) => p.theme.elevation.xs};

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.primary250};
    box-shadow: ${(p) => p.theme.elevation.sm};
  }
`;

const borderStyles = css`
  background: transparent;
  color: ${(p) => p.theme.colors.primary500};
  border: 1.5px solid ${(p) => p.theme.colors.borderStrong};

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.colors.violet600};
    background: rgba(116, 104, 214, 0.06);
  }
`;

const outlineStyles = css`
  background: transparent;
  color: ${(p) => p.theme.colors.primary500};
  border: none;

  &:hover:not(:disabled) {
    background: rgba(47, 48, 113, 0.06);
  }
`;

const variantStyles = (buttonType?: ButtonTypeT) => {
  switch (buttonType) {
    case 'primary':
    case 'with-icon':
      return solidStyles;
    case 'secondary':
      return softStyles;
    case 'grey':
      return greyStyles;
    case 'border':
      return borderStyles;
    default:
      return outlineStyles;
  }
};

const Container = styled.button<ContainerProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 46px;
  padding: 0 20px;
  border: none;
  border-radius: ${(p) => p.theme.borderRadii.pill}px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    background ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    background-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    opacity ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  ${(p) => variantStyles(p.buttonType)}

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.985);
  }

  &:focus-visible {
    box-shadow: ${(p) => p.theme.elevation.focus};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: none;
    }
  }
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
`;

const CustomIcon = styled(Image)`
  flex-shrink: 0;
`;

interface CustomButtonProps extends ComponentProps<typeof Container> {
  label: string;
  customIconName?: string;
  customIconWidth?: number;
  customIconHeight?: number;
  textColor?: string;
  loading?: boolean;
}

export const CustomButton = ({
  label,
  buttonType,
  onClick,
  disabled = false,
  children,
  customIconName,
  customIconWidth,
  customIconHeight,
  loading,
  textColor,
  ...props
}: CustomButtonProps) => {
  const {colors} = useTheme();

  const isSolid = buttonType === 'primary' || buttonType === 'with-icon';
  const resolvedColor =
    textColor ?? (isSolid ? colors.neutral100 : colors.primary500);

  return (
    <Container
      disabled={disabled || loading}
      onClick={onClick}
      aria-busy={loading || undefined}
      {...props}
      buttonType={buttonType}
    >
      <Label className="text-m14" style={{color: resolvedColor}}>
        {buttonType === 'with-icon' ? (
          customIconName !== undefined ? (
            <CustomIcon
              src={`/icons/${customIconName}.svg`}
              width={customIconWidth}
              height={customIconHeight}
              alt={customIconName}
            />
          ) : children ? (
            children
          ) : (
            <svg
              width="17"
              height="15"
              viewBox="0 0 17 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 7.5C0 9.35652 0.737498 11.137 2.05025 12.4497C3.36301 13.7625 5.14348 14.5 7 14.5C8.85889 14.5 10.64 13.7689 11.9778 12.4778L10.8111 11.3111C10.3213 11.8295 9.73034 12.2418 9.07483 12.5228C8.41933 12.8037 7.71317 12.9472 7 12.9444C2.14667 12.9444 -0.28 7.08 3.15 3.65C6.58 0.22 12.4444 2.65444 12.4444 7.5H10.1111L13.2222 10.6111H13.3L16.3333 7.5H14C14 5.64348 13.2625 3.86301 11.9497 2.55025C10.637 1.2375 8.85652 0.5 7 0.5C5.14348 0.5 3.36301 1.2375 2.05025 2.55025C0.737498 3.86301 0 5.64348 0 7.5Z"
                fill="currentColor"
              />
            </svg>
          )
        ) : null}

        {label}
      </Label>

      {loading ? <LoadingDots size={5} color={resolvedColor} /> : null}
    </Container>
  );
};
